// AdminPhraseOverridesPage.js — master-admin JSON editor for the
// Yabbit Suggest-Focus coaching phrase library. Shows shipped defaults
// side-by-side with the current overrides, lets the admin edit the JSON,
// preview the diff, and apply.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { MessageSquare, Loader2, Save, RefreshCw, AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPhraseOverridesPage = () => {
  const { token } = useAuth();
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [jsonText, setJsonText] = useState('{}');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diff, setDiff] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/phrase-overrides`, { headers });
      setDefaults(r.data.defaults || {});
      setOverrides(r.data.overrides || {});
      setUpdatedAt(r.data.updated_at || null);
      setUpdatedBy(r.data.updated_by || null);
      setJsonText(JSON.stringify(r.data.overrides || {}, null, 2));
      setDiff(null);
    } catch (e) {
      console.error(e);
      setError('Failed to load current overrides.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const parseIncoming = () => {
    try {
      const parsed = JSON.parse(jsonText || '{}');
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        setError('JSON must be an object keyed by bucket name.');
        return null;
      }
      setError('');
      return parsed;
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
      return null;
    }
  };

  const runPreview = useCallback(async () => {
    const incoming = parseIncoming();
    if (!incoming) return;
    setPreviewLoading(true);
    try {
      const r = await axios.post(`${API}/admin/phrase-overrides/preview`, { buckets: incoming }, { headers });
      setDiff(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonText, headers]);

  const runSave = useCallback(async () => {
    const incoming = parseIncoming();
    if (!incoming) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/phrase-overrides`, { buckets: incoming }, { headers });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonText, headers, load]);

  const resetToDefaults = () => {
    if (!window.confirm('This will clear ALL overrides and revert to shipped defaults. Continue?')) return;
    setJsonText('{}');
    setDiff(null);
  };

  const seedFromDefaults = () => {
    // Convenience — paste the shipped defaults into the editor so the admin
    // can tweak one bucket without recreating the whole schema from scratch.
    setJsonText(JSON.stringify(defaults, null, 2));
    setDiff(null);
  };

  const defaultBucketKeys = Object.keys(defaults);

  return (
    <AdminLayout>
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-phrase-overrides-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-teal-400" />
          Coaching Phrase Overrides
        </h1>
        <p className="text-sm text-white/60 mt-1 max-w-3xl">
          JSON overrides for the Yabbit <code className="text-xs bg-white/10 px-1.5 py-0.5 rounded">Suggest Focus</code> phrase library.
          Edits are merged on top of the shipped defaults at runtime, so partial overrides fall back to the shipped copy field-by-field.
          Cache TTL is 60s — you can Save then re-open a call detail page to see the new phrases live.
        </p>
        {(updatedAt || updatedBy) && (
          <p className="text-xs text-white/40 mt-2">
            Last saved: {updatedAt ? new Date(updatedAt).toLocaleString() : '—'}{updatedBy ? ` by ${updatedBy}` : ''}
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Shipped defaults reference */}
        <div className="lg:col-span-1 bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="font-semibold text-white mb-3">Shipped bucket keys ({defaultBucketKeys.length})</h3>
          <p className="text-xs text-white/50 mb-3">Reference the exact keys — spelling matters.</p>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {defaultBucketKeys.map((k) => (
              <details key={k} className="border border-white/10 rounded-lg" data-testid={`default-bucket-${k}`}>
                <summary className="cursor-pointer text-sm px-3 py-2 hover:bg-white/5 font-mono text-teal-300">{k}</summary>
                <div className="px-3 pb-3 text-xs text-white/70 space-y-1.5">
                  {defaults[k].avoid && (
                    <div><span className="text-red-400 font-semibold">Avoid:</span> {defaults[k].avoid.join(', ')}</div>
                  )}
                  {defaults[k].practise && (
                    <div>
                      <span className="text-teal-300 font-semibold">Practise:</span>
                      <ul className="list-disc pl-4 mt-1 space-y-0.5">
                        {defaults[k].practise.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </div>
                  )}
                  {defaults[k].why && (
                    <div className="italic text-white/50 mt-1">{defaults[k].why}</div>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-white">Overrides JSON</h3>
              <div className="flex items-center gap-2 text-xs">
                <button onClick={seedFromDefaults} className="text-teal-300 hover:underline" data-testid="seed-from-defaults-btn">
                  Copy defaults →
                </button>
                <span className="text-white/20">·</span>
                <button onClick={resetToDefaults} className="text-red-400 hover:underline flex items-center gap-1" data-testid="reset-overrides-btn">
                  <RotateCcw className="w-3 h-3" /> Reset to shipped
                </button>
              </div>
            </div>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              spellCheck={false}
              rows={16}
              className="w-full font-mono text-xs bg-[#0A0F1C] text-slate-100 border border-white/10 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder='{ "opening": { "avoid": ["…"], "practise": ["…", "…"], "why": "…" } }'
              data-testid="phrase-overrides-textarea"
            />
            {error && (
              <div className="mt-3 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-200" data-testid="phrase-overrides-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={runPreview}
                disabled={previewLoading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-lg disabled:opacity-50 transition-colors"
                data-testid="preview-diff-btn"
              >
                {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Preview diff
              </button>
              <button
                onClick={runSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-teal-500 hover:bg-teal-400 text-white rounded-lg disabled:opacity-50 transition-colors"
                data-testid="save-overrides-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save overrides
              </button>
              {savedFlash && (
                <span className="inline-flex items-center gap-1 text-teal-300 text-sm font-semibold" data-testid="phrase-overrides-saved">
                  <CheckCircle2 className="w-4 h-4" /> Saved & cache flushed
                </span>
              )}
              {loading && <Loader2 className="w-4 h-4 animate-spin text-white/40 ml-auto" />}
            </div>
          </div>

          {/* Diff preview */}
          {diff && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="phrase-overrides-diff">
              <h3 className="font-semibold text-white mb-3">
                Diff — {diff.changed_count || 0} bucket{diff.changed_count === 1 ? '' : 's'} affected
              </h3>
              {(diff.diff || []).length === 0 ? (
                <p className="text-sm text-white/50 italic">No changes vs currently-saved overrides.</p>
              ) : (
                <div className="space-y-3">
                  {diff.diff.map((d, i) => (
                    <div key={i} className="border border-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${d.action === 'added' ? 'bg-teal-500/15 text-teal-300' : d.action === 'removed' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>
                          {d.action}
                        </span>
                        <span className="font-mono text-white/80">{d.bucket}</span>
                        {d.fields_changed && d.fields_changed.length > 0 && (
                          <span className="text-xs text-white/50">Fields: {d.fields_changed.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminPhraseOverridesPage;
